import {
  type GameUiLook,
  resolveValue,
  resolveValues,
  UI_FONT_STACKS,
  type UiCorners,
  type UiScreen,
  type UiSize,
  type UiTarget,
  type UiView,
} from '@mogzo/shared';
import type { CSSProperties } from 'react';

/** The screen size the panels are drawn for: sheets on a phone, side panels and cards on the others. */
export type Device = UiSize;

/** `#rrggbb` at an opacity, for tints of a look's colours. */
export function withAlpha(hex: string, alpha: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** Corners as CSS, in its order: top left, top right, bottom right, bottom left. */
export function cornersCss(c: UiCorners): string {
  return `${c.tl}px ${c.tr}px ${c.br}px ${c.bl}px`;
}

/** How long the achievement pop-up stays, in the view it shows in. */
export function popupSeconds(look: GameUiLook, device: Device, screen: UiScreen = 'game'): number {
  return resolveValue(look.views, `${device}.${screen}`, 'popup', 'seconds').value;
}

/** Everything one panel (or the pop-up) draws with in a view, worked out from a look once. */
export function uiTokens(look: GameUiLook, view: UiView, target: UiTarget) {
  const c = look.colours;
  const v = resolveValues(look.views, view, target);
  const innerRadius = Math.max(0, v.corners.tl - 8);
  const button = (kind: 'primary' | 'secondary' | 'quiet'): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: v.btnHeight,
    padding: '0 18px',
    border: kind === 'secondary' ? `1px solid ${c.line}` : 0,
    borderRadius: v.btnRadius,
    background: kind === 'primary' ? c.accent : 'transparent',
    color: kind === 'primary' ? c.onAccent : kind === 'quiet' ? c.muted : c.text,
    font: 'inherit',
    fontWeight: 700,
    textDecoration: 'none',
    cursor: 'pointer',
  });
  return {
    look,
    values: v,
    colours: c,
    base: v.textSize,
    font: UI_FONT_STACKS[look.font],
    corners: v.corners,
    innerRadius,
    buttonRadius: v.btnRadius,
    gap: v.gap,
    padding: v.pad,
    dim: withAlpha('#0a081a', v.dim / 100),
    panel: {
      background: c.panel,
      color: c.text,
      fontFamily: UI_FONT_STACKS[look.font],
      fontSize: v.textSize,
      lineHeight: 1.4,
      boxSizing: 'border-box',
      padding: v.pad,
      display: 'flex',
      flexDirection: 'column',
      gap: v.gap,
      boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
    } satisfies CSSProperties,
    primary: button('primary'),
    secondary: button('secondary'),
    quiet: button('quiet'),
    field: {
      background: c.field,
      borderRadius: innerRadius,
    } satisfies CSSProperties,
    list: {
      flex: 1,
      minHeight: 0,
      margin: 0,
      padding: 0,
      listStyle: 'none',
      overflowY: 'auto',
      scrollbarWidth: v.scrollbar === 'hidden' ? 'none' : v.scrollbar === 'thin' ? 'thin' : 'auto',
      scrollbarColor: `${c.muted} transparent`,
    } satisfies CSSProperties,
    progressTrack: {
      display: 'block',
      height: v.progress,
      borderRadius: v.progress / 2,
      background: c.line,
      overflow: 'hidden',
    } satisfies CSSProperties,
    switchTrack: (on: boolean): CSSProperties => ({
      position: 'relative',
      flex: 'none',
      width: 50,
      height: 30,
      padding: 0,
      border: 0,
      borderRadius: v.switches === 'round' ? 999 : 7,
      background: on ? c.accent : c.line,
      cursor: 'pointer',
    }),
    switchKnob: (on: boolean): CSSProperties => ({
      position: 'absolute',
      top: 4,
      left: on ? 24 : 4,
      width: 22,
      height: 22,
      borderRadius: v.switches === 'round' ? '50%' : 4,
      background: '#ffffff',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      transition: 'left 0.15s ease',
    }),
  };
}

export type UiTokens = ReturnType<typeof uiTokens>;
